import React, { Dispatch, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Tabs,
  Switch,
  Select,
  Space,
  notification,
} from 'antd';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import Swal from 'sweetalert2';
import mask, { formatPhoneNumber, unmask } from '../../../../../../utils/mask';
import ReactInputMask from 'react-input-mask';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../../store';
import { Action } from 'redux';
import { cadMotoristaServices } from '../../../../../cadastros/cadastroMotorista/services/cadMotoristaServices';
import { getMotoristaPortal } from './services';
import { IMotorista } from '../../../../../../store/ducks/motorista/types';
import { TextLabel } from 'components/TextLabel';
import TitlePage from 'components/TitlePage';
import { cpfValidation, phoneValidation } from 'utils/validator';

interface Props {
  setSelectedMotorista?: any;
  disabled: boolean;
}

const ModalMotorista: React.FC<Props> = ({ disabled, setSelectedMotorista }) => {
  const { Option } = Select;
  const [form] = Form.useForm();
  const [formCreate] = Form.useForm();
  const { TabPane } = Tabs;
  const dispatch = useDispatch<Dispatch<Action>>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [value, setValue] = useState();
  const inscricaoAtiva = useSelector((state: ApplicationState) => state.auth.inscricaoAtiva);
  const [motoristas, setMotoristas] = useState<IMotorista[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    getMotoristaPortal
      .getMotoristaPortal(inscricaoAtiva)
      .then((res) => {
        setMotoristas(res?.data.motoristas);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  }, [dispatch, inscricaoAtiva]);

  const handleOk = (e: any) => {
    formCreate.validateFields().then(async (e) => {
      const isValidCPF = cpfValidation(e?.cnpjCpf);

      if (!isValidCPF) return;

      const isValidPhone = phoneValidation(e.telefone);

      if (!isValidPhone) {
        return notification.error({
          message: 'Telefone inválido',
          placement: 'bottomRight',
        });
      }

      const motorista: any = {
        nome: e?.nome,
        cpf: unmask(e?.cnpjCpf),
        mopp: e?.mopp,
        habilitado: e?.habilitado,
        telefone: e?.telefone,
      };

      try {
        await cadMotoristaServices.criaMotorista(motorista, inscricaoAtiva).then((res) => {
          Swal.fire({
            title: 'Motorista cadastrado com sucesso!',
            html: '',
            icon: 'success',
            showConfirmButton: false,
            timer: 1500,
          });

          setValue(res.data.motorista.nome);
          setSelectedMotorista(res?.data?.motorista?.codMotorista);
          handleCancel();
        });
      } catch (error: any) {
        if (error.response.data.Erro) {
          Swal.fire({
            title: 'Opss...',
            html: `${error.response?.data?.Erro}`,
            icon: 'warning',
          });
        } else {
          Swal.fire({
            title: 'Não foi possivel cadastrar o motorista, por favor tente novamente!',
            html: '',
            icon: 'warning',
            showConfirmButton: true,
          });
        }
      }
    });
  };

  const onFinish = (values: any) => {
    setValue(values.codMotorista.split('-')[0]);
    setSelectedMotorista(Number(values.codMotorista.split('-')[1]));
    handleCancel();
  };

  const handleCancel = () => {
    form.resetFields();
    formCreate.resetFields();
    setIsModalVisible(false);
  };

  return (
    <>
      <Modal
        centered
        title={<TitlePage>Motorista</TitlePage>}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        maskClosable={true}
        closable={true}
        destroyOnClose={true}
        closeIcon={<CloseOutlined />}
        keyboard={true}
        width={850}
        footer={[]}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="Motoristas cadastrados" key="1">
            <Form form={form} onFinish={onFinish} layout="vertical">
              {!loading ? (
                <Form.Item name="codMotorista" label={<TextLabel>Selecione o motorista</TextLabel>}>
                  <Select
                    filterOption={(inputValue, element) =>
                      element?.props.children
                        .toString()
                        .toLowerCase()
                        .replace(/\.|-/g, '')
                        .includes(inputValue?.replace(/\.|-/g, '').toLowerCase())
                    }
                    showSearch
                    placeholder="Selecione o motorista"
                  >
                    {motoristas?.map((item, index) => {
                      return (
                        <Option
                          title={item?.nome}
                          className="optionselect"
                          value={`${mask('camelCase', item?.nome)}-${item?.codMotorista}`}
                          key={item?.codMotorista}
                          style={{ fontWeight: 600 }}
                        >
                          {mask('camelCase', item?.nome)}
                          <br />
                          {mask('cpfCnpj', item?.cpf)}
                          <br />
                          CNH "{item?.habilitado}"
                          <br />
                          {item?.mopp === true ? 'Posui MOPP' : ''}
                          <hr />
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              ) : (
                <LoadingOutlined />
              )}
              <Col md={24} sm={24} xs={24} style={{ textAlign: 'end' }}>
                <Space>
                  <Button type="default" onClick={handleCancel}>
                    Cancelar
                  </Button>
                  <Button type="primary" key="back" size="middle" htmlType="submit" data-cy="btnOk">
                    OK
                  </Button>
                </Space>
              </Col>
            </Form>
          </TabPane>

          <TabPane tab="Novo motorista" key="2">
            <Form form={formCreate}>
              <Row gutter={24}>
                <Col md={12} sm={24} xs={24}>
                  <TextLabel>Nome completo:</TextLabel>
                  <Form.Item
                    name="nome"
                    rules={[
                      { required: true, message: 'Digite o nome completo' },
                      { min: 5, message: 'Digite o nome completo' },
                      { max: 70, message: 'Digite o nome completo' },
                    ]}
                  >
                    <Input
                      name="nome"
                      type="text"
                      minLength={5}
                      placeholder="Digite o nome completo"
                      maxLength={70}
                    />
                  </Form.Item>
                </Col>

                <Col md={12} sm={24} xs={24}>
                  <TextLabel>CPF:</TextLabel>
                  <Form.Item
                    name="cnpjCpf"
                    rules={[{ required: true, message: 'Por favor digite o número do CPF!' }]}
                  >
                    <ReactInputMask
                      className="ant-input"
                      mask={'999.999.999-99' || '99.999.999/9999-99'}
                      maskPlaceholder={null}
                      name="cnpjCpf"
                      placeholder="999.999.999-99"
                    />
                  </Form.Item>
                </Col>

                <Col md={12} sm={24} xs={24}>
                  <TextLabel>Habilitação:</TextLabel>
                  <Form.Item name="habilitado" label="">
                    <Select defaultValue="" style={{ width: '100%' }}>
                      <Option value="A">A</Option>
                      <Option value="B">B</Option>
                      <Option value="AB">AB</Option>
                      <Option value="C">C</Option>
                      <Option value="AC">AC</Option>
                      <Option value="D">D</Option>
                      <Option value="AD">AD</Option>
                      <Option value="E">E</Option>
                      <Option value="AE">AE</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col md={12} sm={24} xs={24}>
                  <TextLabel>Telefone:</TextLabel>
                  <Form.Item
                    name="telefone"
                    label=""
                    rules={[{ required: true, message: 'Por favor digite o número do telefone!' }]}
                  >
                    <Input
                      name="telefone"
                      type="text"
                      maxLength={15}
                      placeholder="Digite o número do telefone"
                      onChange={(e) =>
                        formCreate.setFieldsValue({ telefone: formatPhoneNumber(e.target.value) })
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col md={24} sm={24} xs={24}>
                  <TextLabel>Possui MOPP:</TextLabel>
                  <Form.Item name="mopp" label="" initialValue={false}>
                    <Switch
                      defaultChecked={false}
                      checkedChildren="SIM"
                      unCheckedChildren="NÃO"
                      style={{ width: '100px' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Col md={24} sm={24} xs={24} style={{ textAlign: 'end' }}>
                <Space>
                  <Button type="default" onClick={handleCancel}>
                    Cancelar
                  </Button>
                  <Button type="primary" size="middle" htmlType="submit" onClick={handleOk}>
                    OK
                  </Button>
                </Space>
              </Col>
            </Form>
          </TabPane>
        </Tabs>
      </Modal>
      <Button
        data-cy="motorista"
        style={{
          width: '100%',
        }}
        title={`${value}`}
        type="dashed"
        onClick={() => setIsModalVisible(true)}
        disabled={disabled}
      >
        <span
          style={{
            width: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {!value ? <span style={{ color: '#bbb' }}>Selecione um motorista</span> : value}
        </span>
      </Button>
    </>
  );
};

export default ModalMotorista;
